import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Menu, MenuLabel, MenuContent, MenuItem } from '../../components';
import { handleSwitchLanguage } from '../../ducks/ui.duck';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';

import css from './LanguageSelector.module.css';

// Language configurations with flag codes
const LANGUAGES = [
  { code: 'sv', name: 'Svenska', flagCode: 'SE', label: 'Swedish' },
  { code: 'en', name: 'English', flagCode: 'GB', label: 'English' },
  { code: 'de', name: 'Deutsch', flagCode: 'DE', label: 'German' },
  { code: 'es', name: 'Español', flagCode: 'ES', label: 'Spanish' },
  { code: 'fr', name: 'Français', flagCode: 'FR', label: 'French' },
  { code: 'it', name: 'Italiano', flagCode: 'IT', label: 'Italian' },
];

// Create window.emitter if it doesn't exist
if (typeof window !== 'undefined') {
  if (!window.emitter) {
    window.emitter = {
      listeners: {},
      emit(event, data) {
        if (this.listeners[event]) {
          this.listeners[event].forEach(callback => callback(data));
        }
      },
      on(event, callback) {
        if (!this.listeners[event]) {
          this.listeners[event] = [];
        }
        this.listeners[event].push(callback);
      },
    };
  }
}

const LanguageSelectorComponent = props => {
  const { onHandleSwitchLanguage, currentUser, language, onUpdateProfile, onMobile, intl } = props;

  const [isMenuOpen, setMenuOpen] = useState(false);

  // Initialize language from user profile or session storage
  useEffect(() => {
    const savedLanguage =
      currentUser?.attributes?.profile?.protectedData?.language ||
      (typeof window !== 'undefined' && window.sessionStorage?.getItem('sessionLanguage')) ||
      language;

    if (language !== savedLanguage) {
      onHandleSwitchLanguage(savedLanguage);
    }
  }, []);

  const handleLanguageSwitch = async langCode => {
    setMenuOpen(false);

    // Switch language in Redux state
    await onHandleSwitchLanguage(langCode);

    // Emit event for other components
    if (typeof window !== 'undefined') {
      window.emitter.emit('sessionLanguage', langCode);
    }

    // Persist language server-side session (non-blocking, fallback to local storage if fails)
    try {
      await fetch('/api/session-language', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language: langCode }),
      });
    } catch (e) {
      // Fallback to sessionStorage if server call fails
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('sessionLanguage', langCode);
      }
    }

    // Update user profile protectedData if logged in
    // CRITICAL: Wait for profile update to complete BEFORE reloading
    if (currentUser?.id?.uuid) {
      try {
        await onUpdateProfile({
          protectedData: {
            language: langCode,
          },
        });
        // Give the server a moment to persist the change
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('Failed to update user language preference:', error);
        // Still continue with reload, sessionStorage will be used
      }
    } else if (typeof window !== 'undefined') {
      // Ensure we have session storage value if server-side session not used
      window.sessionStorage.setItem('sessionLanguage', langCode);
    }

    // Reload page to apply translations
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  // Get current language configuration
  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  return (
    <div className={onMobile ? css.rootMobile : css.root}>
      <Menu isOpen={isMenuOpen} onToggleActive={setMenuOpen}>
        <MenuLabel
          className={css.menuLabel}
          isOpenClassName={css.menuLabelOpen}
          aria-label={intl.formatMessage({ id: 'LanguageSelector.ariaLabel' })}
        >
          <span className={css.flagIcon} data-country={currentLang.flagCode}></span>
          <span className={css.languageCode}>{currentLang.code.toUpperCase()}</span>
        </MenuLabel>
        <MenuContent className={onMobile ? css.menuContentMobile : css.menuContent}>
          {LANGUAGES.map(lang => (
            <MenuItem key={lang.code}>
              <button
                className={classNames(css.languageButton, {
                  [css.activeLanguage]: lang.code === language,
                })}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleLanguageSwitch(lang.code);
                }}
                aria-label={`${lang.label} language`}
              >
                <span className={css.flagIcon} data-country={lang.flagCode}></span>
                <span className={css.languageName}>{lang.name}</span>
              </button>
            </MenuItem>
          ))}
        </MenuContent>
      </Menu>
    </div>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { language } = state.ui;
  return {
    currentUser,
    language,
  };
};

const mapDispatchToProps = dispatch => ({
  onHandleSwitchLanguage: newLanguage => dispatch(handleSwitchLanguage(newLanguage)),
  onUpdateProfile: data => dispatch(updateProfile(data)),
});

const LanguageSelector = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(LanguageSelectorComponent);

export default LanguageSelector;
